import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  InputBase,
  Typography,
  FormControl,
  FormLabel,
  FormControlLabel,
  Radio,
  RadioGroup,
} from '@mui/material';
import { isEngagementEngine, type EngagementEngine } from '../../../hooks/advisory/advisoryEngine';
import { useUntypedTranslation } from '../../../hooks/useUntypedTranslation';
import type { ReviewProgressStage } from '../../../hooks/advisory/advisoryReview';
import { focusRing, mono, pillAction, pressable, meta } from '../../../styles/visualLanguage';
import { AdvisoryEngagementShell } from './AdvisoryEngagementShell';

const labels = ['preparing', 'understandingDetails', 'shapingEngagement'] as const;

/** Presentation only: the engagement route continues to own the preparation request. */
export function AdvisoryIntakeScreen({
  question,
  onQuestionChange,
  onSubmit,
  busy = false,
  stage = 'preparing',
  readOnly = false,
  actionDisabled = false,
  actionLabel,
  actionTestId,
  animateEntry = false,
  engine,
  onEngineChange,
  children,
}: {
  question: string;
  onQuestionChange?: (question: string) => void;
  onSubmit?: () => void;
  busy?: boolean;
  stage?: ReviewProgressStage;
  readOnly?: boolean;
  actionDisabled?: boolean;
  actionLabel?: string;
  actionTestId?: string;
  animateEntry?: boolean;
  engine?: EngagementEngine;
  onEngineChange?: (engine: EngagementEngine) => void;
  children?: React.ReactNode;
}) {
  const { t } = useUntypedTranslation();
  const hintId = useId();
  const engineId = useId();
  const actionRow = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const [{ buttonWidth, labelWidth }, setWidths] = useState({ buttonWidth: 320, labelWidth: 268 });
  useLayoutEffect(() => {
    const row = actionRow.current;
    const pill = button.current;
    if (!row || !pill) return;
    const measure = () => {
      const target = Math.min(320, row.clientWidth || 320);
      const style = window.getComputedStyle(pill);
      const content = Math.max(
        0,
        target - (parseFloat(style.paddingLeft) || 0) - (parseFloat(style.paddingRight) || 0)
      );
      setWidths(previous =>
        previous.buttonWidth === target && previous.labelWidth === content
          ? previous
          : { buttonWidth: target, labelWidth: content }
      );
    };
    // Keep the label at its final width while the pill opens, so narrow initial
    // widths do not briefly turn a translated label into a tall text column.
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(row);
    return () => observer.disconnect();
  }, [busy]);
  const [expanded, setExpanded] = useState(busy && !animateEntry);
  const [takingLonger, setTakingLonger] = useState(false);
  useEffect(() => {
    if (!busy) {
      setExpanded(false);
      setTakingLonger(false);
      return;
    }
    // A fresh route starts at Continue's width, then expands after the first paint.
    let secondFrame = 0;
    const frame = window.requestAnimationFrame(() => {
      secondFrame = window.requestAnimationFrame(() => setExpanded(true));
    });
    const timer = window.setTimeout(() => setTakingLonger(true), 30000);
    return () => {
      window.cancelAnimationFrame(frame);
      window.cancelAnimationFrame(secondFrame);
      window.clearTimeout(timer);
    };
  }, [busy]);
  const disabled = busy || actionDisabled || !onSubmit || (!readOnly && !question.trim());
  const submit = () => {
    if (!disabled) onSubmit?.();
  };
  return (
    <AdvisoryEngagementShell
      position="01"
      total={null}
      eyebrowKey="advisory:intake.eyebrow"
      titleKey="advisory:intake.title"
      hintKey="advisory:intake.hint"
      actions={
        <Box ref={actionRow} sx={{ width: '100%', minWidth: 0 }}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, minWidth: 0 }}
          >
            <Box
              data-busy={busy ? 'true' : undefined}
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                minWidth: 0,
                alignItems: 'center',
                gap: 2,
                maxWidth: '100%',
                '&:hover .review-timing, &:focus-within .review-timing': {
                  opacity: 1,
                  transform: 'translate(0, 0)',
                },
                '@media (max-width: 600px)': {
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: 1,
                },
                '@media (hover: none)': {
                  '&[data-busy="true"]': {
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: 1,
                  },
                  '&[data-busy="true"] .review-timing': { opacity: 1, transform: 'none' },
                },
              }}
            >
              <Button
                variant="contained"
                disableRipple
                ref={button}
                onClick={submit}
                disabled={!busy && disabled}
                aria-disabled={disabled}
                aria-busy={busy}
                aria-describedby={hintId}
                data-testid={actionTestId || 'advisory-intake-continue'}
                sx={[
                  pillAction,
                  pressable,
                  focusRing,
                  theme => ({
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0, 1fr)',
                    overflow: 'hidden',
                    whiteSpace: 'normal',
                    flexShrink: 0,
                    maxWidth: '100%',
                    width: busy ? (expanded ? buttonWidth : 116) : undefined,
                    minWidth: busy
                      ? expanded
                        ? `min(${buttonWidth}px, 100%)`
                        : '116px'
                      : undefined,
                    transition: 'width 200ms ease, min-width 200ms ease',
                    ...(busy
                      ? {
                          backgroundColor: theme.palette.primary.main,
                          color: theme.palette.primary.contrastText,
                          cursor: 'wait',
                          '&:hover, &:active': {
                            backgroundColor: theme.palette.primary.main,
                            transform: 'none',
                            boxShadow: 'none',
                          },
                        }
                      : {}),
                    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                  }),
                ]}
              >
                <Box
                  sx={{
                    gridArea: '1 / 1',
                    width: busy ? labelWidth : undefined,
                    minWidth: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1.25,
                  }}
                >
                  {busy && (
                    <CircularProgress
                      aria-hidden="true"
                      size={14}
                      thickness={5}
                      color="inherit"
                      sx={{
                        flexShrink: 0,
                        '@media (prefers-reduced-motion: reduce)': {
                          animation: 'none',
                          '& circle': { animation: 'none' },
                        },
                      }}
                    />
                  )}
                  {busy ? (
                    <Box
                      sx={{ display: 'grid', minWidth: 0, gridTemplateColumns: 'minmax(0, 1fr)' }}
                    >
                      {labels.map(label => (
                        <Box
                          component="span"
                          key={label}
                          aria-hidden={stage !== label}
                          sx={{
                            gridArea: '1 / 1',
                            opacity: stage === label ? 1 : 0,
                            transition: 'opacity 160ms ease',
                            '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                          }}
                        >
                          {t(`advisory:intake.${label}`)}
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <span>{t(actionLabel || 'advisory:intake.continue')}</span>
                  )}
                </Box>
              </Button>
              <Typography
                component="span"
                id={hintId}
                className="review-timing"
                sx={[
                  mono,
                  theme => ({
                    fontSize: theme.visualPrimitives.kbdHintSize,
                    color: theme.colors.inkTertiary,
                    whiteSpace: 'nowrap',
                    opacity: 0,
                    transform: 'translate(-8px, 8px)',
                    transition: 'opacity 200ms ease, transform 200ms ease',
                    '@media (prefers-reduced-motion: reduce)': {
                      transition: 'none',
                      transform: 'none',
                    },
                  }),
                ]}
              >
                {t('advisory:intake.timingHint')}
              </Typography>
            </Box>
            {!readOnly && !busy && (
              <Typography
                component="span"
                sx={[
                  mono,
                  theme => ({
                    fontSize: theme.visualPrimitives.kbdHintSize,
                    color: theme.colors.inkTertiary,
                    ml: 'auto',
                  }),
                ]}
              >
                {t('advisory:intake.enterHint')}
              </Typography>
            )}
          </Box>
          {/* Outside aria-busy so assistive technology can announce real stages immediately. */}
          {busy && (
            <Box
              role="status"
              aria-live="polite"
              aria-atomic="true"
              aria-label={t(`advisory:intake.${stage}`)}
              data-testid="advisory-review-loading"
              sx={{
                position: 'absolute',
                width: '1px',
                height: '1px',
                overflow: 'hidden',
                clipPath: 'inset(50%)',
                whiteSpace: 'nowrap',
              }}
            >
              {t(`advisory:intake.${stage}`)}
            </Box>
          )}
          {busy && takingLonger && (
            <Typography role="status" sx={[meta, { mt: 2 }]}>
              {t('advisory:intake.takingLonger')}
            </Typography>
          )}
          {children && <Box sx={{ mt: 2 }}>{children}</Box>}
        </Box>
      }
    >
      <InputBase
        fullWidth
        data-testid="advisory-intake-input"
        readOnly={readOnly || busy}
        inputProps={{ 'aria-label': t('advisory:intake.inputAriaLabel') }}
        placeholder={t('advisory:intake.placeholder')}
        value={question}
        onChange={event => {
          if (!busy && !readOnly) onQuestionChange?.(event.target.value);
        }}
        onKeyDown={event => {
          if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
            event.preventDefault();
            submit();
          }
        }}
        sx={theme => ({
          fontSize: theme.visualPrimitives.inputSize,
          lineHeight: theme.visualPrimitives.inputLineHeight,
          mt: theme.visualPrimitives.inputGap,
          color: theme.colors.inkPrimary,
          borderBottom: `1px solid ${
            busy || readOnly ? theme.colors.lavenderDeep : theme.visualPrimitives.border
          }`,
          '& .MuiInputBase-input': { padding: theme.visualPrimitives.inputPaddingY },
          '& .MuiInputBase-input::placeholder': { color: theme.colors.inkTertiary, opacity: 1 },
          '&:focus-within': { borderBottomColor: theme.colors.lavenderDeep },
        })}
      />
      {engine && onEngineChange && (
        <FormControl disabled={busy || readOnly} sx={{ mt: 3 }}>
          <FormLabel id={`${engineId}-label`} sx={[meta]}>
            {t('advisory:engine.label')}
          </FormLabel>
          <RadioGroup
            row
            name="engagement-engine"
            aria-labelledby={`${engineId}-label`}
            aria-describedby={`${engineId}-hint`}
            value={engine}
            onChange={(_, value) => {
              if (isEngagementEngine(value)) onEngineChange(value);
            }}
          >
            <FormControlLabel
              value="v3"
              control={<Radio sx={[focusRing]} />}
              label={t('advisory:engine.v3')}
            />
            <FormControlLabel
              value="astra"
              control={<Radio sx={[focusRing]} />}
              label={t('advisory:engine.astra')}
            />
          </RadioGroup>
          <Typography id={`${engineId}-hint`} sx={[meta]}>
            {t('advisory:engine.hint')}
          </Typography>
        </FormControl>
      )}
    </AdvisoryEngagementShell>
  );
}
